import React from "react";
import { useToast } from "@chakra-ui/react";
import { StepperModal } from "../../../_components/Modals";
import { CompanyInformationStep } from "./steps/1-company-information";
import { ContactDetailsStep } from "./steps/2-contact-details";
import { JobConsultationStep } from "./steps/3-job-consultation";
import {
  RecruitmentConsultationProvider,
  useRecruitmentConsultation,
} from "../context/recruitment-consultation.context";
import {
  CompanyInformationFormData,
  ContactDetailsFormData,
  JobConsultationFormData,
} from "../schemas/recruitment-consultation.schema";
import { logEvent } from "../../../helpers/analytics";

interface RecruitmentConsultationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const RecruitmentConsultationModalContent: React.FC<{
  onClose: () => void;
}> = ({ onClose }) => {
  const { 
    currentStep, 
    nextStep, 
    previousStep, 
    submitForm, 
    resetForm,
    updateCompanyInfo,
    updateContactDetails,
    updateJobConsultation,
    getAllFormData,
    getCompanyInfo,
    getContactDetails,
    getJobConsultation,
    isLoading,
  } = useRecruitmentConsultation();

  const toast = useToast();

  const getStepTitle = () => {
    switch (currentStep) {
      case 1:
        return "Tell us about your company";
      case 2:
        return "Contact details";
      case 3:
        return "Job consultation questions";
      default:
        return "Recruitment Consultation";
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleCompanyInfoComplete = (data: CompanyInformationFormData) => {
    updateCompanyInfo(data);
    nextStep();
  };

  const handleContactDetailsComplete = (data: ContactDetailsFormData) => {
    updateContactDetails(data);
    nextStep();
  };

  const handleJobConsultationComplete = async (data: JobConsultationFormData) => {
    updateJobConsultation(data);
    
    // Get all form data and submit
    const allFormData = { ...getAllFormData(), ...data };
    
    // Type assertion to ensure all required fields are present
    const submitData = {
      ...allFormData,
      expectedStartDate: allFormData.expectedStartDate!.toISOString(),
    };
    
    await submitForm({
      variables: {
        recruitmentConsultationDto: submitData as any, // Use type assertion to handle the DTO conversion
      },
      onCompleted: () => {
        toast({
          title: "Request Submitted Successfully",
          description:
            "We'll contact you soon to discuss your recruitment needs.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });

        handleClose();
        logEvent("recruitment_consultation_submitted");
      },
      onError: (error) => {
        toast({
          title: "Submission Failed",
          description:
            "Please try again or contact support if the issue persists.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });

        logEvent("recruitment_consultation_error", { error: error.message });
      },
    });
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <CompanyInformationStep 
            onStepComplete={handleCompanyInfoComplete}
            isLoading={isLoading}
          />
        );
      case 2:
        return (
          <ContactDetailsStep 
            onStepComplete={handleContactDetailsComplete}
            onPrevious={previousStep}
            isLoading={isLoading}
          />
        );
      case 3:
        return (
          <JobConsultationStep 
            onStepComplete={handleJobConsultationComplete}
            onPrevious={previousStep}
            isLoading={isLoading}
          />
        );
      default:
        return null;
    }
  };

  return (
    <StepperModal
      isOpen={true}
      onClose={handleClose}
      title={getStepTitle()}
      onSubmit={() => {}} // Not used since each step handles its own submission
      currentStep={currentStep}
      totalSteps={3}
      isLoading={isLoading}
      onReset={resetForm}
    >
      {renderCurrentStep()}
    </StepperModal>
  );
};

export const RecruitmentConsultationModal: React.FC<RecruitmentConsultationModalProps> =
  ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    return (
      <RecruitmentConsultationProvider>
        <RecruitmentConsultationModalContent onClose={onClose} />
      </RecruitmentConsultationProvider>
    );
  };
